import { DatePanel } from "@/components/gantt/date-panel";
import { DragPreview } from "@/components/gantt/drag-preview";
import { GanttChart } from "@/components/gantt/gantt-chart";
import { DashboardHeader } from "@/components/layout/header";
import { chipShapeRaw, pressableRaw } from "@/components/overview/pressable";
import {
  type Domain,
  type Projection,
  type TimeOffBlock,
  type TriageSummary,
  fetchProjection,
  fetchTimeOff,
  fetchTriageSummary,
  sendTriageBatch,
} from "@/lib/api";
import {
  type PlanUndo,
  type Range,
  buildPlanBatch,
  buildPreview,
  chartRange,
  dragBlock,
  isStalePlan,
  phasedProjects,
  phasesOf,
  planOf,
  rangeLabel,
  scopeText,
  stagePlan,
  undoPlan,
  undoPlanLeft,
  weeksIn,
} from "@/lib/gantt";
import { type GanttStored, loadGanttStaged, saveGanttStaged } from "@/lib/gantt-store";
import { DOMAIN_LABEL, DOMAIN_ORDER, isoDay, relativeAge } from "@/lib/overview";
import { newClientBatchId } from "@/lib/triage-store";
import { Box, Spinner, Text } from "@okshaun/components";
import { css } from "@styled-system/css";
import { useCallback, useEffect, useMemo, useState } from "react";

const POLL_MS = 60_000;
const chip = css(pressableRaw, chipShapeRaw);

const sendButton = css({
  fontFamily: "sans",
  fontSize: "[13px]",
  color: "text.inverse",
  bg: "bg.brand.bold",
  border: "none",
  borderRadius: "4",
  px: "12",
  py: "6",
  cursor: "pointer",
  outline: "none",
  _hover: { bg: "bg.brand.bold.hovered" },
  _active: { bg: "bg.brand.bold.pressed" },
  _disabled: { bg: "bg.disabled", color: "text.disabled", cursor: "not-allowed" },
  _focusVisible: { boxShadow: "[0 0 0 2px var(--oks-colors-border-focused)]" },
});

const removeButton = css({
  fontFamily: "mono",
  fontSize: "12",
  color: "text.subtlest",
  bg: "transparent",
  border: "none",
  cursor: "pointer",
  px: "4",
  outline: "none",
  _hover: { color: "text.danger" },
  _focusVisible: { color: "text.danger" },
});

// The P12 snackbar: one small line, bottom-left, a text "Undo" and a dismiss ×.
const snackbar = css({
  fontFamily: "sans",
  position: "fixed",
  bottom: "16",
  left: "16",
  zIndex: "[1000]",
  display: "flex",
  alignItems: "center",
  gap: "4",
  maxW: "[calc(100vw - 32px)]",
  pl: "16",
  pr: "4",
  py: "4",
  borderRadius: "4",
  bg: "bg.neutral.bold",
  color: "text.inverse",
  boxShadow: "overlay",
  fontSize: "[14px]",
  lineHeight: "[20px]",
  whiteSpace: "nowrap",
});
const snackText = css({ mr: "8", overflow: "hidden", textOverflow: "ellipsis" });
const snackUndo = css({
  fontFamily: "sans",
  fontSize: "[14px]",
  fontWeight: "medium",
  color: "[#8ab4f8]",
  bg: "transparent",
  border: "none",
  borderRadius: "4",
  px: "8",
  py: "6",
  cursor: "pointer",
  outline: "none",
  _hover: { bg: "[rgba(255, 255, 255, 0.08)]" },
  _focusVisible: { bg: "[rgba(255, 255, 255, 0.12)]" },
});
const snackClose = css({
  fontSize: "[14px]",
  color: "text.inverse",
  opacity: "0.7",
  bg: "transparent",
  border: "none",
  borderRadius: "4",
  w: "[28px]",
  h: "[28px]",
  cursor: "pointer",
  outline: "none",
  _hover: { opacity: "1", bg: "[rgba(255, 255, 255, 0.08)]" },
  _focusVisible: { opacity: "1", bg: "[rgba(255, 255, 255, 0.12)]" },
});

const code = css({
  display: "block",
  fontFamily: "mono",
  fontSize: "12",
  lineHeight: "[18px]",
  color: "text.subtle",
  bg: "surface",
  border: "default",
  borderRadius: "4",
  p: "12",
  m: "0",
  whiteSpace: "pre",
  overflowX: "auto",
});

const IS_MAC =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
const UNDO_KEY = IS_MAC ? "⌘Z" : "Ctrl+Z";

function useNarrow(query = "(max-width: 640px)"): boolean {
  const get = () => typeof window !== "undefined" && window.matchMedia?.(query).matches === true;
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return narrow;
}

type SendState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent"; id: string; count: number; duplicate: boolean; obsidianRunning: boolean }
  | { kind: "error"; message: string };

export function GanttPage() {
  const today = isoDay(new Date());
  const narrow = useNarrow();
  const dayW = narrow ? 12 : 16;
  const leftW = narrow ? 112 : 220;

  const [projection, setProjection] = useState<Projection | null>(null);
  const [summary, setSummary] = useState<TriageSummary | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [timeOff, setTimeOff] = useState<TimeOffBlock[]>([]);
  const [timeOffFailed, setTimeOffFailed] = useState(false);
  const [domain, setDomain] = useState<Domain | "all">("all");
  const [stored, setStored] = useState<GanttStored>(loadGanttStaged);
  const [draft, setDraft] = useState<{ id: string; range: Range } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [undo, setUndo] = useState<PlanUndo | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [sendState, setSendState] = useState<SendState>({ kind: "idle" });

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([fetchProjection(), fetchTriageSummary().catch(() => null)]);
      setProjection(p);
      if (s) setSummary(s);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load");
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Staged changes survive a reload. If storage is blocked, staging still works for this tab.
  useEffect(() => {
    saveGanttStaged(stored);
  }, [stored]);

  const pendingPlans = summary?.pendingPlans ?? {};
  const allPhases = useMemo(() => (projection ? phasesOf(projection) : []), [projection]);
  const allGroups = useMemo(() => (projection ? phasedProjects(projection) : []), [projection]);
  const groups = useMemo(
    () => (projection ? phasedProjects(projection, domain) : []),
    [projection, domain]
  );
  const phasedAll = useMemo(() => allGroups.flatMap((g) => g.phases), [allGroups]);
  const shownPhases = useMemo(() => groups.flatMap((g) => g.phases), [groups]);

  // The chart covers the plans, the staged ranges and the pending ranges.
  const chart = useMemo(() => {
    const shown = new Set(shownPhases.map((p) => p.id));
    const extra = [
      ...Object.values(stored.items)
        .filter((s) => shown.has(s.phaseId))
        .map((s) => s.to),
      ...Object.entries(pendingPlans)
        .filter(([id]) => shown.has(id))
        .map(([, p]) => ({ start: p.start, end: p.end })),
    ];
    return chartRange(shownPhases, today, extra);
  }, [shownPhases, stored, pendingPlans, today]);
  const weeks = useMemo(() => weeksIn(chart), [chart]);

  // Time off is an optional overlay. A failure leaves the chart working.
  useEffect(() => {
    let live = true;
    fetchTimeOff(chart.start, chart.end)
      .then((b) => {
        if (!live) return;
        setTimeOff(b);
        setTimeOffFailed(false);
      })
      .catch(() => {
        if (!live) return;
        setTimeOff([]);
        setTimeOffFailed(true);
      });
    return () => {
      live = false;
    };
  }, [chart.start, chart.end]);

  // The undo window: re-render while it runs, then close it.
  useEffect(() => {
    if (!undo) return;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (undoPlanLeft(undo, t) === 0) setUndo(null);
    }, 200);
    return () => clearInterval(id);
  }, [undo]);

  const phaseById = useMemo(() => new Map(allPhases.map((p) => [p.id, p])), [allPhases]);
  const pendingIds = useMemo(() => new Set(Object.keys(pendingPlans)), [pendingPlans]);

  const onDraft = useCallback((id: string, r: Range | null) => {
    setDraft(r ? { id, range: r } : null);
  }, []);

  const onDrop = useCallback(
    (id: string, r: Range) => {
      const p = phaseById.get(id);
      setDraft(null);
      if (!p || dragBlock(p, pendingIds)) return;
      const res = stagePlan(stored.items, p, r);
      if (!res) return;
      setStored((s) => ({ ...s, items: res.items }));
      setUndo(res.undo);
      setNow(Date.now());
      setSendState({ kind: "idle" });
    },
    [phaseById, pendingIds, stored.items]
  );

  const onSelect = useCallback((id: string) => {
    setSelected((cur) => (cur === id ? null : id));
    setDraft(null);
  }, []);

  const doUndo = useCallback(() => {
    if (!undo || undoPlanLeft(undo) === 0) return;
    setStored((s) => ({ ...s, items: undoPlan(s.items, undo) }));
    setUndo(null);
    setDraft(null);
  }, [undo]);

  // Cmd+Z (Ctrl+Z off a Mac) undoes the last staged change while its window is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = IS_MAC ? e.metaKey : e.ctrlKey;
      if (!mod || e.shiftKey || e.altKey || e.key.toLowerCase() !== "z") return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable))
        return;
      if (!undo) return;
      e.preventDefault();
      doUndo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, doUndo]);

  const stagedList = useMemo(
    () => Object.values(stored.items).sort((a, b) => a.stagedAt.localeCompare(b.stagedAt)),
    [stored]
  );
  const fresh = stagedList.filter((s) => !isStalePlan(s, allPhases));

  const send = async () => {
    if (!projection || fresh.length === 0) return;
    const clientBatchId = stored.clientBatchId ?? newClientBatchId();
    if (!stored.clientBatchId) setStored((s) => ({ ...s, clientBatchId }));
    const batch = buildPlanBatch(stagedList, projection, clientBatchId);
    if (batch.decisions.length === 0) return;
    setSendState({ kind: "sending" });
    try {
      const res = await sendTriageBatch(batch);
      const sentIds = new Set(batch.decisions.map((d) => d.taskId));
      setStored((s) => ({
        version: 1,
        items: Object.fromEntries(Object.entries(s.items).filter(([id]) => !sentIds.has(id))),
      }));
      setUndo(null);
      setSendState({
        kind: "sent",
        id: res.id,
        count: res.decisions,
        duplicate: res.duplicate,
        obsidianRunning: res.obsidianRunning,
      });
      load();
    } catch (err) {
      setSendState({
        kind: "error",
        message: err instanceof Error ? err.message : "unknown error",
      });
    }
  };

  // The preview follows the live draft. With no draft, a selected staged phase shows its staged change.
  const preview = useMemo(() => {
    const id = draft?.id ?? selected;
    if (!id) return null;
    const p = phaseById.get(id);
    const plan = p ? planOf(p) : null;
    if (!p || !plan) return null;
    const to = draft?.id === id ? draft.range : stored.items[id]?.to;
    if (!to) return null;
    return buildPreview(p, plan, to, phasedAll, weeks);
  }, [draft, selected, phaseById, stored.items, phasedAll, weeks]);

  const selectedPhase = selected ? phaseById.get(selected) : undefined;
  const selectedPlan = selectedPhase ? planOf(selectedPhase) : null;
  const undoMs = undoPlanLeft(undo, now);
  const problems = projection?.phaseProblems ?? [];

  return (
    <Box
      display="flex"
      flexDir="column"
      gap="20"
      pt="12"
      pb="20"
      px={{ base: "16", md: "20" }}
      maxW="[1400px]"
      mx="auto"
      minH="screen"
      minW="0"
    >
      <DashboardHeader
        trailing={
          projection ? (
            <Text textStyle="mono.xs" color="text.subtlest" hideBelow="md">
              bee read the vault {relativeAge(projection.generatedAt)}
            </Text>
          ) : undefined
        }
      />

      <Box display="flex" flexDir="column" gap="6">
        <Text as="h1" fontSize="20" fontWeight="semibold" color="text.bold" m="0">
          Gantt
        </Text>
        {projection && (
          <Text fontSize="14" color="text.subtle">
            {scopeText(projection)} Add a "### Name" heading and a "Plan:" line under ## Tasks to
            show a project here.
          </Text>
        )}
      </Box>

      {loadError && (
        <Text textStyle="mono.sm" color="text.danger">
          gantt: {loadError}
        </Text>
      )}
      {!projection && !loadError && (
        <Box display="flex" alignItems="center" gap="4">
          <Spinner size="sm" />
          <Text textStyle="mono.xs">Loading phases…</Text>
        </Box>
      )}

      {projection && (
        <>
          <Box display="flex" gap="12" flexWrap="wrap" alignItems="center">
            <Box
              as="fieldset"
              display="flex"
              gap="6"
              flexWrap="wrap"
              border="none"
              p="0"
              m="0"
              minW="0"
              aria-label="Domain"
            >
              {(["all", ...DOMAIN_ORDER] as Array<Domain | "all">).map((d) => (
                <button
                  key={d}
                  type="button"
                  className={chip}
                  aria-pressed={domain === d}
                  onClick={() => setDomain(d)}
                >
                  {d === "all" ? "All" : DOMAIN_LABEL[d]}
                </button>
              ))}
            </Box>
            <Box ml={{ base: "0", md: "auto" }} display="flex" gap="8" alignItems="center">
              <Text textStyle="mono.xs" color="text.subtle">
                {fresh.length} staged
              </Text>
              <button
                type="button"
                className={sendButton}
                disabled={fresh.length === 0 || sendState.kind === "sending"}
                onClick={send}
              >
                {sendState.kind === "sending" ? "Sending…" : `Send ${fresh.length}`}
              </button>
            </Box>
          </Box>

          {(stagedList.length > 0 || sendState.kind !== "idle") && (
            <Box as="section" aria-label="Staged changes" display="flex" flexDir="column" gap="4">
              {stagedList.map((s) => {
                const stale = isStalePlan(s, allPhases);
                return (
                  <Box key={s.phaseId} display="flex" gap="8" alignItems="baseline" flexWrap="wrap">
                    <Text fontSize="14" color={stale ? "text.subtlest" : "text"}>
                      {s.name}: {rangeLabel(s.to)} (was {rangeLabel(s.from)})
                    </Text>
                    {stale && (
                      <Text textStyle="mono.xs" color="text.warning">
                        changed in the vault, drag again
                      </Text>
                    )}
                    <button
                      type="button"
                      className={removeButton}
                      aria-label={`Remove the staged change to ${s.name}`}
                      onClick={() =>
                        setStored((st) => {
                          const items = { ...st.items };
                          delete items[s.phaseId];
                          return { ...st, items };
                        })
                      }
                    >
                      ✕
                    </button>
                  </Box>
                );
              })}
              {sendState.kind === "sent" && (
                <Text as="output" textStyle="mono.xs" color="text.success">
                  Sent {sendState.count} change{sendState.count === 1 ? "" : "s"} (batch{" "}
                  {sendState.id}
                  {sendState.duplicate ? ", already saved" : ""}). They apply on the work Mac
                  {sendState.obsidianRunning ? "" : " once Obsidian runs"}.
                </Text>
              )}
              {sendState.kind === "error" && (
                <Text as="output" textStyle="mono.xs" color="text.danger">
                  Send failed: {sendState.message}
                </Text>
              )}
            </Box>
          )}

          {groups.length === 0 ? (
            <Box display="flex" flexDir="column" gap="8" maxW="[560px]">
              <Text fontSize="14" color="text">
                No phased projects yet.
              </Text>
              <pre className={code}>{"### Discover\nPlan: 2026-11-02 → 2026-11-16"}</pre>
            </Box>
          ) : (
            <Box
              display="grid"
              gridTemplateColumns={{ base: "[minmax(0, 1fr)]", lg: "[minmax(0, 1fr) 300px]" }}
              gap="20"
              alignItems="start"
              minW="0"
            >
              <Box display="flex" flexDir="column" gap="6" minW="0">
                <GanttChart
                  groups={groups}
                  allPhases={phasedAll}
                  chart={chart}
                  weeks={weeks}
                  today={today}
                  timeOff={timeOff}
                  dayW={dayW}
                  leftW={leftW}
                  narrow={narrow}
                  staged={stored.items}
                  draft={draft}
                  pendingPlans={pendingPlans}
                  selected={selected}
                  onDraft={onDraft}
                  onDrop={onDrop}
                  onSelect={onSelect}
                />
                {timeOffFailed && (
                  <Text textStyle="mono.xs" color="text.subtlest">
                    time off unavailable
                  </Text>
                )}
              </Box>
              <Box display="flex" flexDir="column" gap="12" minW="0">
                <DragPreview preview={preview} />
                {selectedPhase && selectedPlan && (
                  <DatePanel
                    key={selectedPhase.id}
                    phase={selectedPhase}
                    plan={selectedPlan}
                    current={stored.items[selectedPhase.id]?.to ?? selectedPlan}
                    block={dragBlock(selectedPhase, pendingIds)}
                    onDraft={(r) => onDraft(selectedPhase.id, r)}
                    onStage={(r) => {
                      onDrop(selectedPhase.id, r);
                    }}
                    onClose={() => setSelected(null)}
                  />
                )}
              </Box>
            </Box>
          )}

          <Box as="section" aria-label="Problems" display="flex" flexDir="column" gap="6">
            <Text as="h2" fontSize="14" fontWeight="medium" color="text.bold" m="0">
              Problems ({problems.length})
            </Text>
            {problems.map((p) => (
              <Text key={`${p.path}:${p.line}:${p.kind}`} textStyle="mono.xs" color="text.warning">
                {p.path}:{p.line} · {p.kind} · {p.text}
              </Text>
            ))}
          </Box>

          {undo && undoMs > 0 && (
            <output className={snackbar} aria-live="polite">
              <span className={snackText}>{undo.label}</span>
              <button
                type="button"
                className={snackUndo}
                onClick={doUndo}
                title={`Undo (${UNDO_KEY})`}
              >
                Undo
              </button>
              <button
                type="button"
                className={snackClose}
                onClick={() => setUndo(null)}
                aria-label="Dismiss"
              >
                ✕
              </button>
            </output>
          )}
        </>
      )}
    </Box>
  );
}
